// Components/ExamInvitationsModal.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import './ExamInvitationsModal.css';

const EXAM_API_URL = 'https://api.avtechbmt.cloud/api/exams';

/**
 * Props:
 *  - exam     : { _id, title }
 *  - onClose  : fn
 */
export default function ExamInvitationsModal({ exam, onClose }) {
  const { token } = useAuth();
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const headers = useCallback(
    () => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token || localStorage.getItem('token')}`,
    }),
    [token]
  );

  const fetchInvitations = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${EXAM_API_URL}/${exam._id}/invitations`, {
        headers: headers(),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setInvitations(data.data || []);
      } else {
        setError(data.message || 'Failed to load invitations.');
      }
    } catch {
      setError('Network error loading invitations.');
    } finally {
      setLoading(false);
    }
  }, [exam._id, headers]);

  useEffect(() => {
    fetchInvitations();
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [fetchInvitations]);

  const handleRevoke = async (invitationId, email) => {
    if (!window.confirm(`Revoke invitation for ${email}?`)) return;
    try {
      const res = await fetch(
        `${EXAM_API_URL}/invitations/${invitationId}/revoke`,
        { method: 'PATCH', headers: headers() }
      );
      const data = await res.json();
      if (res.ok && data.success) {
        fetchInvitations();
      } else {
        setError(data.message || 'Failed to revoke.');
      }
    } catch {
      setError('Network error revoking invitation.');
    }
  };

  const formatDate = (d) =>
    d ? new Date(d).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—';

  return (
    <div className="eim-overlay" onClick={onClose}>
      <div className="eim-modal" onClick={(e) => e.stopPropagation()}>
        <header className="eim-header">
          <div>
            <span className="eim-eyebrow">INVITATIONS</span>
            <h3>{exam.title}</h3>
            <p className="eim-sub">
              {loading
                ? 'Loading…'
                : `${invitations.length} invitation${invitations.length === 1 ? '' : 's'}`}
            </p>
          </div>
          <button className="eim-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>

        {error && <div className="eim-alert eim-alert-error">⚠️ {error}</div>}

        <div className="eim-body">
          {loading ? (
            <div className="eim-state">Loading invitations…</div>
          ) : invitations.length === 0 ? (
            <div className="eim-state">
              <span className="icon">✉️</span>
              <span>No invitations sent yet for this exam.</span>
            </div>
          ) : (
            <div className="eim-table-wrap">
              <table className="eim-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Expires</th>
                    <th>Accepted</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {invitations.map((inv) => (
                    <tr key={inv._id}>
                      <td className="eim-email">{inv.email}</td>
                      <td>
                        <span className={`eim-status eim-status-${inv.status}`}>
                          {inv.status}
                        </span>
                      </td>
                      <td>{formatDate(inv.expiresAt)}</td>
                      <td>{inv.acceptedAt ? formatDate(inv.acceptedAt) : '—'}</td>
                      <td className="eim-actions">
                        {inv.status !== 'revoked' && (
                          <button
                            className="eim-revoke"
                            onClick={() => handleRevoke(inv._id, inv.email)}
                          >
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <footer className="eim-footer">
          <button className="eim-btn eim-btn-primary" onClick={onClose}>
            Close
          </button>
        </footer>
      </div>
    </div>
  );
}